import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { useSelector } from 'react-redux';

import Badge from 'components/field/Badge';
import Button from 'components/field/Button';
import Alert from 'components/global/Alert';
import SelectButton from 'components/global/SelectButton';
import Skeleton from 'components/global/Skeleton';
import Typography from 'components/global/Typography';

import useLocale from 'hooks/useLocale';

import * as S from '../OTOLink.styled';

const PICKUP_OPTIONS = [
	{
		id: 'branchDropOff',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/dropoffNearestBranch.png',
		title: 'dropoffToTheNearestBranch',
		subtitle: 'dropoffToTheNearestBranchSubtitle',
		badge: <Badge variant="pill" color="teel" text="fastest" />
	},
	{
		id: 'pickupByDc',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/pickupByCourier.png',
		title: 'pickupByTheShippingCourier',
		subtitle: 'pickupByTheShippingCourierSubtitle'
	}
];

const validateOptions = {
	shouldValidate: true,
	shouldDirty: true
};

const ShippingMethodTab = () => {
	const {
		setValue,
		watch,
		control,
		clearErrors,
		formState: { errors }
	} = useFormContext();
	const { LanguageCode } = useLocale();

	const deliveryCompanies = useSelector((state) => state.otoLinkSettings.deliveryCompanies);
	const isLoading = useSelector((state) => state.otoLinkSettings.isLoadingDeliveryCompanies);

	const pickupType = useWatch({ control, name: 'pickupType' });

	const onDeliveryCompanyChange = (value) => {
		const currentValues = watch('allowedDCIds') || [];
		clearErrors('allowedDCIds');
		setValue(
			'allowedDCIds',
			currentValues.includes(value)
				? currentValues.filter((method) => method !== value)
				: [...currentValues, value],
			validateOptions
		);
	};

	const handleSelectAll = () => {
		clearErrors('allowedDCIds');
		if (deliveryCompanies.length === watch('allowedDCIds')?.length) {
			setValue('allowedDCIds', [], validateOptions);
		} else {
			setValue(
				'allowedDCIds',
				deliveryCompanies.map((dc) => dc.ID),
				validateOptions
			);
		}
	};

	const unselectClosedPickupOption = () => {
		clearErrors('allowedDCIds');
		const allowedDCIds = watch('allowedDCIds') || [];
		const pickupType = watch('pickupType') || [];
		if (pickupType?.length === 0) {
			setValue('allowedDCIds', [], validateOptions);
		} else {
			const result = allowedDCIds.filter((id) => deliveryCompanies.some((dc) => dc.ID === id));
			setValue('allowedDCIds', result, validateOptions);
		}
	};

	useEffect(() => {
		if (!isLoading) {
			unselectClosedPickupOption();
		}
	}, [isLoading]);

	const onPickupTypeChange = (id) => {
		setValue(
			'pickupType',
			pickupType.includes(id) ? pickupType.filter((method) => method !== id) : [...pickupType, id],
			validateOptions
		);
	};

	return (
		<>
			<Alert variant="info" showIcon content="otoLinkSettingsShippingMethodAlert" className="my-4" />

			<Typography variant="p1" fontWeight={500} className="my-4">
				pickupOptions
			</Typography>

			<S.PickupOptionsButtonContainer>
				{PICKUP_OPTIONS.map((option) => (
					<SelectButton
						key={option.id}
						checked={pickupType?.includes?.(option.id)}
						onClick={() => onPickupTypeChange(option.id)}
						image={option.image}
						title={option.title}
						description={option.subtitle}
						badge={option.badge}
					/>
				))}
			</S.PickupOptionsButtonContainer>

			<S.Flex align="center" className="mt-5 mb-3">
				<Typography variant="p2" fontWeight={500}>
					<Trans i18nKey="shippingCompanies" values={{ count: watch('allowedDCIds')?.length || 0 }} />
				</Typography>
				<Button label="selectAll" variant="blueBorderless" onClick={handleSelectAll} />
			</S.Flex>
			{pickupType && (
				<S.DeliveryCompanyContainer>
					{isLoading
						? Array(6)
								.fill(0)
								.map((_, index) => (
									<SelectButton
										key={`skeleton-${index}`}
										image={<Skeleton width="48px" height="48px" borderRadius="8px" />}
										title={<Skeleton width="120px" height="20px" borderRadius="4px" />}
									/>
								))
						: deliveryCompanies.map((dc) => (
								<SelectButton
									key={dc.ID}
									checked={watch('allowedDCIds')?.includes(dc.ID)}
									onClick={() => onDeliveryCompanyChange(dc.ID)}
									image={dc.logo}
									title={LanguageCode === 'arabicValue' ? dc.nameAr : dc.name}
									error={errors?.allowedDCIds}
								/>
							))}
				</S.DeliveryCompanyContainer>
			)}
		</>
	);
};

export default ShippingMethodTab;
